import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ColDef } from 'ag-grid-enterprise';

import { isDefined, isEmpty, isNotDefined } from '@enterprise/core';

import type { ListSegmentCellProps } from '@components/grid/renderers/types/list-segment-renderer.params';
import type { PercentCellProps } from '@components/grid/renderers/types/percent-cell-renderer.params';
import type { NthCalculatorGridContext } from '@components/nth-calculator/types/nth-calculator-grid-context.type';
import type { NthCalculatorPopoutRow } from '@components/nth-calculator/types/nth-calculator-popout-row.type';
import type { NthCalculatorRow } from '@components/nth-calculator/types/nth-calculator-row.type';

import DateCellRenderer from '@components/grid/renderers/date-cell.renderer';
import ListSegmentRenderer from '@components/grid/renderers/list-segment-cell-renderer';
import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';
import PercentCellRenderer from '@components/grid/renderers/percent-cell.renderer';
import ApproachCell from '@components/nth-calculator/components/approach-cell';
import DropTwentileCell from '@components/nth-calculator/components/drop-twentile-cell';
import NotesCell from '@components/nth-calculator/components/notes-cell';
import NthInputCell from '@components/nth-calculator/components/nth-input-cell';
import TargetInputCell from '@components/nth-calculator/components/target-input-cell';
import { useGetMailingSummary } from '@components/nth-calculator/hooks/get-mailing-summary.hook';
import { useGetTwentileCountsByMailing } from '@components/nth-calculator/hooks/get-twentile-count-by-mailing.hook';
import { useNthCalculatorStore } from '@components/nth-calculator/stores/nth-calculator-store';
import { useDefaultGridOptions } from '@hooks/grid/default-grid-options.hook';
import { useDefaultGridTheme } from '@hooks/grid/default-grid-theme.hook';

import type { UseNthCalculatorGridConfigurationResult } from './types/nth-calculator-grid-configuration.type';

const recomputeDerived = (row: NthCalculatorRow, updates: Partial<NthCalculatorRow>): NthCalculatorRow => {
  const merged = { ...row, ...updates } as NthCalculatorRow;
  const hasSelectedTwentiles = merged.selectedTwentileValues.length > 0;
  const hasNthValue = isDefined(merged.nthValue);

  const newTarget = hasSelectedTwentiles
    ? merged.firstOutput -
      merged.selectedTwentileValues.reduce((sum, twentileNum) => {
        const found = merged.twentiles.find(({ twentile }) => {
          return twentile === twentileNum;
        });

        return sum + (found?.count ?? 0);
      }, 0)
    : isDefined(merged.nthValue)
      ? merged.firstOutput - merged.nthValue
      : merged.firstOutput;

  const approach: NthCalculatorRow['approach'] = hasSelectedTwentiles
    ? 'dropped-by-twentile'
    : hasNthValue
      ? 'manual'
      : 'not-set';

  const finalPct = merged.receivedQuantity !== 0 ? newTarget / merged.receivedQuantity : null;

  const nthValue = hasSelectedTwentiles ? merged.firstOutput - newTarget : merged.nthValue;

  return { ...merged, approach, finalPct, newTarget, nthValue };
};

export const useNthCalculatorGridConfiguration = ({
  mailingId,
  nthThresholdPercentage,
  onOverThresholdChange,
  packageTarget,
}: {
  mailingId?: number;
  nthThresholdPercentage: number;
  onOverThresholdChange?: (isOver: boolean) => void;
  packageTarget: number;
}): UseNthCalculatorGridConfigurationResult => {
  const defaultGridOptions = useDefaultGridOptions<NthCalculatorPopoutRow>({ hasStatusBar: false });
  const theme = useDefaultGridTheme();

  const isPopout = useNthCalculatorStore(({ isPopout }) => {
    return isPopout;
  });

  const { data: rows, dataUpdatedAt, isFetching, isLoading } = useGetTwentileCountsByMailing(mailingId);
  const { data: summaryRows } = useGetMailingSummary(isPopout ? mailingId : undefined);

  const clearDraft = useNthCalculatorStore(({ clearDraft }) => {
    return clearDraft;
  });

  const setDraft = useNthCalculatorStore(({ setDraft }) => {
    return setDraft;
  });

  const draft = useNthCalculatorStore(({ drafts }) => {
    return isDefined(mailingId) ? drafts[mailingId] : undefined;
  });

  const [rowData, setRowData] = useState<NthCalculatorRow[]>([]);
  const rowDataRef = useRef<NthCalculatorRow[]>([]);
  const hasSeededRef = useRef(false);

  rowDataRef.current = rowData;

  useEffect(() => {
    if (hasSeededRef.current || dataUpdatedAt === 0 || isNotDefined(rows)) {
      return;
    }

    if (isDefined(draft)) {
      setRowData(
        rows.map((row) => {
          const draftRow = draft.rows.find((dr) => {
            return dr.mailingListId === row.mailingListId;
          });

          if (isNotDefined(draftRow)) {
            return row;
          }

          return recomputeDerived(row, {
            notes: draftRow.notes,
            nthValue: draftRow.nthValue,
            selectedTwentileValues: draftRow.selectedTwentileValues,
          });
        })
      );
    } else {
      setRowData(rows);
    }

    hasSeededRef.current = true;
  }, [rows, dataUpdatedAt, draft]);

  const persistDraft = useCallback(
    (rows: NthCalculatorRow[]) => {
      if (isNotDefined(mailingId)) {
        return;
      }

      setDraft(
        mailingId,
        rows.map(({ mailingListId, notes, nthValue, selectedTwentileValues }) => {
          return { mailingListId, notes, nthValue, selectedTwentileValues };
        })
      );
    },
    [mailingId, setDraft]
  );

  const handleRowUpdate = useCallback(
    (mailingListId: number, updates: Partial<NthCalculatorRow>) => {
      const next = rowDataRef.current.map((row) => {
        if (row.mailingListId !== mailingListId) {
          return row;
        }

        return recomputeDerived(row, updates);
      });

      setRowData(next);
      persistDraft(next);
    },
    [persistDraft]
  );

  const handleDropBottom4 = useCallback(() => {
    const next = rowDataRef.current.map((row) => {
      const selectedTwentileValues = row.twentiles
        .filter(({ twentile }) => {
          return twentile >= 1 && twentile <= 4;
        })
        .map(({ twentile }) => {
          return twentile;
        });

      return recomputeDerived(row, { nthValue: null, selectedTwentileValues });
    });

    setRowData(next);
    persistDraft(next);
  }, [persistDraft]);

  const handleReset = useCallback(() => {
    setRowData((prev) => {
      return prev.map((row) => {
        return recomputeDerived(row, { notes: '', nthValue: null, selectedTwentileValues: [] });
      });
    });
    if (isDefined(mailingId)) {
      clearDraft(mailingId);
    }
  }, [clearDraft, mailingId]);

  const getMailingListUpdates = useCallback(() => {
    return rowData.map((row) => {
      return { mailingListId: row.mailingListId, targetQuantity: row.newTarget };
    });
  }, [rowData]);

  const totalNewTarget = useMemo(() => {
    return rowData.reduce((sum, row) => {
      return sum + row.newTarget;
    }, 0);
  }, [rowData]);

  const isOverThreshold = useMemo(() => {
    if (packageTarget <= 0) {
      return false;
    }

    return totalNewTarget > packageTarget * (1 + nthThresholdPercentage / 100);
  }, [nthThresholdPercentage, packageTarget, totalNewTarget]);

  useEffect(() => {
    onOverThresholdChange?.(isOverThreshold);
  }, [isOverThreshold, onOverThresholdChange]);

  const context = useMemo((): NthCalculatorGridContext => {
    return { handleRowUpdate, isOverThreshold };
  }, [handleRowUpdate, isOverThreshold]);

  const mergedRowData = useMemo((): NthCalculatorPopoutRow[] => {
    if (!isPopout || isNotDefined(summaryRows) || isEmpty(summaryRows)) {
      return rowData as NthCalculatorPopoutRow[];
    }

    const summaryById = new Map(
      summaryRows.map((summary) => {
        return [summary.mailingListId, summary];
      })
    );

    return rowData.map((row) => {
      const summary = summaryById.get(row.mailingListId);

      if (isNotDefined(summary)) {
        return row as NthCalculatorPopoutRow;
      }

      return { ...summary, ...row };
    });
  }, [rowData, isPopout, summaryRows]);

  const nthCalculatorColumns = useMemo((): ColDef<NthCalculatorPopoutRow>[] => {
    return [
      { field: 'mailCode', hide: true },
      { field: 'mailingListId', hide: true },
      { field: 'receivedQuantity', hide: true },
      {
        cellDataType: false,
        cellRenderer: DropTwentileCell,
        field: 'selectedTwentileValues',
        headerName: 'N-th (By Twentile)',
        sortable: false,
        suppressHeaderMenuButton: true,
        width: 140,
      },
      {
        cellRenderer: NthInputCell,
        field: 'nthValue',
        headerName: 'N-th',
        sortable: false,
        suppressHeaderMenuButton: true,
        width: 95,
      },
      {
        aggFunc: 'sum',
        cellDataType: false,
        cellRenderer: TargetInputCell,
        field: 'newTarget',
        headerClass: 'ag-left-aligned-header',
        headerName: 'Target',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 95,
      },
      {
        cellRenderer: ApproachCell,
        field: 'approach',
        headerName: 'Approach',
        sortable: false,
        suppressHeaderMenuButton: true,
        width: 110,
      },
      {
        cellRenderer: PercentCellRenderer,
        cellRendererParams: (params: PercentCellProps<NthCalculatorPopoutRow, number>) => {
          return {
            ...params,
            grandTotalFields: ['receivedQuantity'],
            maximumDecimals: 1,
            totalFields: ['newTarget'],
          } as PercentCellProps<NthCalculatorPopoutRow, number>;
        },
        field: 'finalPct',
        headerClass: 'ag-right-aligned-header',
        headerName: '%',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 70,
      },
      {
        cellRenderer: NotesCell,
        field: 'notes',
        flex: 1,
        headerName: 'Notes',
        hide: true,
        sortable: false,
        suppressHeaderMenuButton: true,
      },
    ];
  }, []);

  const popoutSummaryColumns = useMemo((): ColDef<NthCalculatorPopoutRow>[] => {
    return [
      {
        cellDataType: 'date',
        cellRenderer: DateCellRenderer,
        field: 'mailDate',
        headerName: 'Mail Date',
        sortable: false,
        suppressHeaderMenuButton: true,
        width: 120,
      },
      {
        field: 'mailCode',
        headerName: 'MailCode',
        sortable: false,
        suppressHeaderMenuButton: true,
        width: 150,
      },
      {
        cellRenderer: ListSegmentRenderer,
        cellRendererParams: (params: ListSegmentCellProps<NthCalculatorPopoutRow>) => {
          return {
            ...params,
            header: 'listDisplayName',
            subHeader: 'listManagerName',
          } as ListSegmentCellProps<NthCalculatorPopoutRow>;
        },
        field: 'listDisplayName',
        headerName: 'List/Segment',
        sortable: false,
        suppressHeaderMenuButton: true,
        width: 220,
        wrapText: true,
      },
      {
        aggFunc: 'sum',
        cellRenderer: NumberCellRenderer,
        field: 'quantityOrdered',
        headerName: 'Requested',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 115,
      },
      {
        aggFunc: 'sum',
        cellRenderer: NumberCellRenderer,
        field: 'quantityReceived',
        headerName: 'Received',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 115,
      },
      {
        aggFunc: 'sum',
        cellClass: ['bg-yellow-200', 'text-yellow-900'],
        cellRenderer: NumberCellRenderer,
        field: 'quantitySuppressed',
        headerClass: ['bg-yellow-900'],
        headerName: 'Suppress',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 115,
      },
      {
        aggFunc: 'sum',
        cellClass: ['bg-yellow-100', 'text-yellow-900'],
        cellRenderer: NumberCellRenderer,
        field: 'quantityDupes',
        headerClass: ['bg-yellow-900'],
        headerName: 'Dupe',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 100,
      },
      {
        aggFunc: 'sum',
        cellClass: ['bg-gray-200', 'text-gray-900'],
        cellRenderer: NumberCellRenderer,
        field: 'quantityFirstOutput',
        headerClass: ['bg-gray-800'],
        headerName: 'First Output',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 120,
      },
      {
        aggFunc: 'sum',
        cellClass: ['bg-teal-300', 'text-teal-900'],
        cellRenderer: NumberCellRenderer,
        field: 'twentile1To4',
        headerClass: ['bg-teal-800'],
        headerName: '1-4%',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 100,
      },
      {
        aggFunc: 'sum',
        cellClass: ['bg-teal-300', 'text-teal-900'],
        cellRenderer: NumberCellRenderer,
        field: 'twentile5To20',
        headerClass: ['bg-teal-800'],
        headerName: '5-20%',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 100,
      },
      {
        aggFunc: 'sum',
        cellClass: ['bg-gray-200', 'text-gray-900'],
        cellRenderer: NumberCellRenderer,
        field: 'nth',
        headerClass: ['bg-gray-800'],
        headerName: 'Current N-th',
        sortable: false,
        suppressHeaderMenuButton: true,
        type: ['numericColumn'],
        width: 120,
      },
    ];
  }, []);

  const columns = useMemo((): ColDef<NthCalculatorPopoutRow>[] => {
    if (!isPopout) {
      return nthCalculatorColumns;
    }

    return [
      ...popoutSummaryColumns,
      ...(nthCalculatorColumns.map((column) => {
        return { ...column, pinned: 'right' };
      }) as ColDef<NthCalculatorPopoutRow>[]),
    ];
  }, [isPopout, nthCalculatorColumns, popoutSummaryColumns]);

  return {
    columns,
    context,
    defaultColumnDefinition: {
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
    },
    defaultGridOptions,
    getMailingListUpdates,
    handleDropBottom4,
    handleReset,
    isLoading: isFetching || isLoading,
    rowData: mergedRowData,
    theme,
  };
};
